import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PageCard } from '@components/common/PageCard.tsx';
import { toastService } from '@services/toast.service.ts';
import { Feature } from './FeaturesTab';
import { SubscriptionPlan } from '../../../types/payment';

interface PlanFeatureMatrixProps {
  fetchPlans: () => Promise<SubscriptionPlan[]>;
  fetchFeatures: () => Promise<Feature[]>;
  fetchPlanFeatures: (planId: number) => Promise<string[]>;
  setPlanFeatures: (planId: number, keys: string[]) => Promise<string[]>;
}

/**
 * Shared "plan × feature" grid — used by both the client and expert configurators
 * (PlanFeatureMatrixTab / ExpertPlanFeatureMatrixTab). The two stay separate
 * components/pages (client vs expert plans are isolated on the backend too),
 * only this presentational+fetch logic is shared to avoid duplicating the grid itself.
 */
export const PlanFeatureMatrix: React.FC<PlanFeatureMatrixProps> = ({ fetchPlans, fetchFeatures, fetchPlanFeatures, setPlanFeatures }) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  // planId -> Set of granted feature keys
  const [grants, setGrants] = useState<Record<number, Set<string>>>({});
  const [loading, setLoading] = useState(false);
  const [pendingCell, setPendingCell] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [plansList, featuresList] = await Promise.all([fetchPlans(), fetchFeatures()]);
        setPlans(plansList);
        setFeatures(featuresList);

        const entries = await Promise.all(
          plansList.map(async (plan) => [plan.id, new Set(await fetchPlanFeatures(plan.id))] as const),
        );
        setGrants(Object.fromEntries(entries));
      } catch {
        toastService.error('Не вдалося завантажити матрицю план × можливість.');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchPlans, fetchFeatures, fetchPlanFeatures]);

  const toggle = async (plan: SubscriptionPlan, feature: Feature) => {
    const cellKey = `${plan.id}:${feature.id}`;
    const current = grants[plan.id] ?? new Set<string>();
    const next = new Set(current);
    next.has(feature.key) ? next.delete(feature.key) : next.add(feature.key);

    setPendingCell(cellKey);
    setGrants((prev) => ({ ...prev, [plan.id]: next }));
    try {
      await setPlanFeatures(plan.id, Array.from(next));
    } catch {
      toastService.error('Не вдалося оновити доступ — повертаю попередній стан.');
      setGrants((prev) => ({ ...prev, [plan.id]: current }));
    } finally {
      setPendingCell(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageCard>
        {features.length === 0 || plans.length === 0 ? (
          <Typography sx={{ mt: 2, textAlign: 'center' }}>
            Спочатку зареєструйте можливості та переконайтесь, що є хоча б один план.
          </Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Можливість</TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.id} align="center">{plan.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature) => (
                  <TableRow key={feature.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{feature.name}</Typography>
                      <Typography variant="caption" color="text.secondary"><code>{feature.key}</code></Typography>
                    </TableCell>
                    {plans.map((plan) => {
                      const cellKey = `${plan.id}:${feature.id}`;
                      return (
                        <TableCell key={plan.id} align="center">
                          <Checkbox
                            checked={grants[plan.id]?.has(feature.key) ?? false}
                            disabled={pendingCell === cellKey}
                            onChange={() => toggle(plan, feature)}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageCard>
    </Box>
  );
};
